import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { get_api, post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import {
  editIcon,
  fileUploadIcon,
  historyIcon,
  saveIcon,
  undoIcon,
  sendMailIcon,
  sortIcon
} from './icons';

const mailTemplates = [
  {
    name: 'Custom',
    subject: '',
    body: ''
  },
  {
    name: 'Request Resume',
    subject: 'Regarding your application',
    body: `Hi [Applicant Name],

Thank you for your interest in joining MyYogaTeacher!

To proceed with the application process, kindly share your updated resume with us at onboarding@myyogateacher.com

Feel free to reach out if you have any questions.

Best regards,
MyYogaTeacher - Onboarding & Support`
  }
];

const STATUS_ORDER = {
  'review: pending': 1,

  'screening: pending': 2,
  'screening: rejected': 3,
  'screening: selected': 4,

  'call: pending': 5,
  'call: scheduled': 6,
  'call: candidate no response': 7,
  'call: candidate will call back': 8,
  'call: candidate not interested': 9,
  'call: rejected': 10,
  'call: selected': 11,

  'demo: pending': 12,
  'demo: scheduled': 13,
  'demo: rejected': 14,
  'demo: selected': 15,

  'training: pending': 16,
  'training: rejected': 17,
  'training: candidate not interested': 18,

  onboarded: 19
};

function TeacherApplication() {
  const [filters, setFilters] = useState({
    dateRange: {
      start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD')
    },
    status: '',
    experience: '',
    minTtcHours: ''
  });

  const [applications, setApplications] = useState([]);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [applicationHistory, setApplicationHistory] = useState([]);
  const [isResumeUploading, setResumeUploading] = useState(null);
  const [showEditPayIcon, setShowEditPayIcon] = useState('');

  useEffect(() => {
    const endpoint = get_api_url('/v2/teacher_recruitement/applications');

    const payload = {
      status: filters.status,
      teaching_experience: filters.experience,
      training_hours: filters.minTtcHours
    };

    if (filters.dateRange.start !== '' && filters.dateRange.end !== '') {
      payload.start_date = moment(filters.dateRange.start).format('YYYY-MM-DD HH:mm:ss');
      payload.end_date = moment(filters.dateRange.end).format('YYYY-MM-DD HH:mm:ss');
    }

    post_api(endpoint, payload, true).then((response) => {
      let applications = response.data.data || [];

      applications = applications.map((app) => {
        const values = calculateApplicationStatus(app);
        app.nextStatusAvailable = values.length > 0;
        return app;
      });

      setApplications(applications);
    });
  }, [filters]);

  useEffect(() => {
    if (selectedApplication == null) return;

    const endpoint = get_api_url(
      `/v2/teacher_recruitement/applications/${selectedApplication.uuid}/history`
    );
    get_api(endpoint, true).then((response) => {
      setApplicationHistory(response.data.data || []);
    });
  }, [selectedApplication]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleDateRangeChange = (type, value) => {
    // console.log("type, value", type, value);
    setFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: value
      }
    }));
  };

  const handleViewMore = (application) => {
    setSelectedApplication(application);
    setShowHistoryModal(true);
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setApplicationHistory([]);
    setSelectedApplication(null);
  };

  const handleFileChange = async (application, file) => {
    // console.log('Selected file:', file);

    if (!file.name.endsWith('.pdf')) {
      alert('Please upload a PDF file.');
      return;
    }

    setResumeUploading(application.uuid);
    const payload = new FormData();
    payload.append('resume', file, file.name);

    const endpoint = get_api_url(
      `/v2/teacher_recruitement/applications/${application.uuid}/resume`
    );

    const response = await post_api(endpoint, payload, true, 'formdata');

    const updated = applications.map((app) => {
      if (app.uuid === application.uuid) {
        app.resume = response.data.resume;
      }
      return app;
    });

    setApplications([...updated]);
    setResumeUploading(null);
  };

  const [editPay, setEditPay] = useState('');

  const handleEditPayChange = (value) => {
    setEditPay(value);
  };

  const handleEditPay = async () => {
    const endpoint = get_api_url(
      `/v2/teacher_recruitement/applications/${showEditPayIcon}/base_pay`
    );

    const payload = {
      base_pay: editPay
    };

    await post_api(endpoint, payload, true);

    const updated = applications.map((app) => {
      if (app.uuid === showEditPayIcon) {
        app.base_pay = editPay;
      }
      return app;
    });

    setApplications([...updated]);
    setEditPay('');
    setShowEditPayIcon('');
  };

  // handle status change
  const [showEditStatus, setShowEditStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  const statusOptions = [
    { value: 'review: pending', label: 'Review Pending' },
    { value: 'screening: pending', label: 'Screening Pending' },
    { value: 'screening: rejected', label: 'Screening Rejected' },
    { value: 'screening: selected', label: 'Screening Selected' },
    { value: 'call: pending', label: 'Call Pending' },
    { value: 'call: scheduled', label: 'Call Scheduled' },
    { value: 'call: candidate no response', label: 'Call - Candidate No Response' },
    { value: 'call: candidate will call back', label: 'Call - Candidate Will Call Back' },
    { value: 'call: candidate not interested', label: 'Call - Candidate Not Interested' },
    { value: 'call: rejected', label: 'Call Rejected' },
    { value: 'call: selected', label: 'Call Selected' },
    { value: 'demo: pending', label: 'Demo Pending' },
    { value: 'demo: scheduled', label: 'Demo Scheduled' },
    { value: 'demo: rejected', label: 'Demo Rejected' },
    { value: 'demo: selected', label: 'Demo Selected' },
    { value: 'training: pending', label: 'Training Pending' },
    { value: 'training: rejected', label: 'Training Rejected' },
    { value: 'training: candidate not interested', label: 'Training - Candidate Not Interested' },
    { value: 'onboarded', label: 'Onboarded' }
  ];

  const workflowComplete = (application) =>
    application.status.includes('onboarded') ||
    application.status.includes('rejected') ||
    application.status.includes('not interested');

  const calculateApplicationStatus = (application) => {
    if (application == null) return statusOptions;

    if (
      application.status.includes('onboarded') ||
      application.status.includes('rejected') ||
      application.status.includes('not interested')
    ) {
      return [];
    }

    if (application.status.startsWith('review:')) {
      return [
        { value: 'screening: pending', label: 'Screening Pending' },
        { value: 'screening: rejected', label: 'Screening Rejected' },
        { value: 'screening: selected', label: 'Screening Selected' }
      ];
    }

    if (
      application.status.startsWith('screening:') ||
      application.status.startsWith('call:') ||
      application.status.startsWith('demo:')
    ) {
      return [
        { value: 'call: pending', label: 'Call Pending' },
        { value: 'call: scheduled', label: 'Call Scheduled' },
        { value: 'call: candidate no response', label: 'Call - Candidate No Response' },
        { value: 'call: candidate will call back', label: 'Call - Candidate Will Call Back' },
        { value: 'call: candidate not interested', label: 'Call - Candidate Not Interested' },
        { value: 'call: rejected', label: 'Call Rejected' },
        { value: 'call: selected', label: 'Call Selected' },
        { value: 'demo: pending', label: 'Demo Pending' },
        { value: 'demo: scheduled', label: 'Demo Scheduled' },
        { value: 'demo: rejected', label: 'Demo Rejected' },
        { value: 'demo: selected', label: 'Demo Selected' },
        { value: 'training: pending', label: 'Training Pending' },
        { value: 'training: rejected', label: 'Training Rejected' },
        {
          value: 'training: candidate not interested',
          label: 'Training - Candidate Not Interested'
        },
        { value: 'onboarded', label: 'Onboarded' }
      ];
    }

    return statusOptions.filter((option) => option.value !== application.status);
  };

  const editStatusOptions = useMemo(
    () => calculateApplicationStatus(showEditStatus),
    [showEditStatus]
  );

  const handleStatusChange = async () => {
    const reason = document.getElementById('reason-input').value;

    if (reason.trim() === '') {
      alert('Please provide a reason for the status change.');
      return;
    }

    if (newStatus === '' || newStatus === showEditStatus.status) {
      alert('Please select a new status.');
      return;
    }

    const endpoint = get_api_url(
      `/v2/teacher_recruitement/applications/${showEditStatus.uuid}/status`
    );

    await post_api(
      endpoint,
      {
        reason,
        status: newStatus
      },
      true
    );

    setShowEditStatus(null);

    document.getElementById('reason-input').value = '';

    const updated = applications.map((app) => {
      if (app.uuid === showEditStatus.uuid) {
        app.status = newStatus;

        const values = calculateApplicationStatus(app);
        app.nextStatusAvailable = values.length > 0;
      }
      return app;
    });

    setApplications([...updated]);
  };

  // custom mail
  const [mailInfo, setMailInfo] = useState({ show: false, subject: '', body: '', to: null });
  const [mailTemplate, setMailTemplate] = useState({ name: 'Custom', subject: '', body: '' });

  const handleSendMail = async () => {
    if (mailInfo.subject.trim() === '' || mailInfo.body.trim() === '') {
      alert('Subject and body cannot be empty.');
      return;
    }

    const endpoint = get_api_url(`/v2/teacher_recruitement/applications/${mailInfo.to.uuid}/mail`);

    const response = await post_api(
      endpoint,
      {
        subject: mailInfo.subject,
        body: mailInfo.body
      },
      true
    );

    setMailInfo({ show: false, subject: '', body: '', to: null });
  };

  const [sortedBy, setSortedBy] = useState({ name: false, cv: false, status: false });

  return (
    <div className="teacher-applications-container p-6">
      {/* Title */}
      <h1 className="text-3xl font-bold mb-6">Teacher Applications</h1>

      {/* Filters Section */}
      <div className="filters-section bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range Filter */}
          <div className="filter-item">
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <div className="flex gap-2">
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.dateRange.start}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                placeholder="Start Date"
              />
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.dateRange.end}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                placeholder="End Date"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Filter */}
          <div className="filter-item">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All</option>
              {statusOptions.map((option) => (
                <option
                  key={`filter-status-${option.value}`}
                  value={option.value}
                  className="capitalize"
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Experience Filter */}
          <div className="filter-item">
            <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.experience}
              onChange={(e) => handleFilterChange('experience', e.target.value)}
            >
              <option value="">All</option>
              <option value="Less than 1 year">Less than 1 year</option>
              <option value="1-3 years">1-3 years</option>
              <option value="3-5 years">3-5 years</option>
              <option value="5-10 years">5-10 years</option>
              <option value="More than 10 years">More than 10 years</option>
            </select>
          </div>

          {/* Minimum TTC Hours Filter */}
          <div className="filter-item">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum TTC Hours
            </label>

            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.minTtcHours}
              onChange={(e) => handleFilterChange('minTtcHours', e.target.value)}
            >
              <option value="">All</option>
              <option value="Less than 200 hours">Less than 200 hours</option>
              <option value="200-300 hours">200-300 hours</option>
              <option value="300-500 hours">300-500 hours</option>
              <option value="More than 500 hours">More than 500 hours</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-section bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applicant{' '}
                  {sortIcon({
                    className: 'w-4 h-4 inline-block ml-1 text-blue-600 cursor-pointer',
                    onClick: () => {
                      const name = !sortedBy.name;

                      const sorted = [...applications].sort((a, b) => {
                        if (name) {
                          return a.full_name.localeCompare(b.full_name);
                        }
                        return b.full_name.localeCompare(a.full_name);
                      });
                      setApplications(sorted);
                      setSortedBy({ ...sortedBy, name });
                    }
                  })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CV{' '}
                  {sortIcon({
                    className: 'w-4 h-4 inline-block ml-1 text-blue-600 cursor-pointer',
                    onClick: () => {
                      const cv = !sortedBy.cv;

                      const sorted = [...applications].sort((a, b) => {
                        const aHasResume = a.resume.toLowerCase() != 'missing';
                        const bHasResume = b.resume.toLowerCase() != 'missing';

                        // 1️⃣ Group by resume presence
                        if (aHasResume !== bHasResume) {
                          if (cv) return aHasResume ? -1 : 1; // non-empty first
                          return aHasResume ? 1 : -1; // non-empty first
                        }

                        // 2️⃣ Stable secondary sort
                        return a.full_name.localeCompare(b.full_name);
                      });

                      setApplications(sorted);
                      setSortedBy({ ...sortedBy, cv });
                    }
                  })}
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Credentials
                                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AI Credentials
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status{' '}
                  {sortIcon({
                    className: 'w-4 h-4 inline-block ml-1 text-blue-600 cursor-pointer',
                    onClick: () => {
                      const status = !sortedBy.status;

                      const sorted = [...applications].sort((a, b) => {
                        const aRank = STATUS_ORDER[a.status] ?? Number.MAX_SAFE_INTEGER;
                        const bRank = STATUS_ORDER[b.status] ?? Number.MAX_SAFE_INTEGER;

                        if (aRank !== bRank) {
                          if (status) {
                            return aRank - bRank;
                          }
                          return bRank - aRank;
                        }

                        // stable secondary sort
                        return a.full_name.localeCompare(b.full_name);
                      });

                      setApplications(sorted);
                      setSortedBy({ ...sortedBy, status });
                    }
                  })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((application) => (
                <tr key={application.uuid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm max-w-[350px]">
                    <div className="flex flex-col gap-1 items-start">
                      <span className="font-medium text-gray-900 capitalize">
                        {application.full_name}
                      </span>
                      <span className="font-medium text-gray-900 capitalize">
                        {application.age} years, {application.gender}
                      </span>
                      <span className="font-medium text-gray-900 capitalize text-left whitespace-pre-line mt-1">
                        {application.location.split(',').join('\n')}
                      </span>

                      <a
                        href={`tel:${application.country_code}${application.mobile}`}
                        className="text-blue-600 hover:underline"
                      >
                        {application.country_code}-{application.mobile}
                      </a>
                      <a
                        href={`mailto:${application.email}`}
                        className="text-blue-600 hover:underline"
                      >
                        {application.email.length > 30
                          ? `${application.email.substring(0, 30)}...`
                          : application.email}
                      </a>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-start">
                    {isResumeUploading === application.uuid ? (
                      <span className="text-gray-500">Uploading...</span>
                    ) : application.resume !== '' && application.resume !== 'Missing' ? (
                      <a
                        href={application.resume}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline cursor-pointer text-blue-600"
                      >
                        View CV
                      </a>
                    ) : !workflowComplete(application) ? (
                      <div className="flex flex-col items-start space-y-2">
                        <label
                          className="cursor-pointer text-blue-400 hover:text-blue-600"
                          htmlFor="file-upload"
                        >
                          {fileUploadIcon({
                            htmlFor: 'file-upload',
                            className: 'cursor-pointer text-blue-400 hover:text-blue-600 w-8 h-8'
                          })}
                        </label>

                        <input
                          id="file-upload"
                          type="file"
                          className="hidden"
                          onChange={(e) => handleFileChange(application, e.target.files[0])}
                        />
                      </div>
                    ) : (
                      <span className="text-gray-500">{application.resume}</span>
                    )}
                  </td>
                  {/* <td className="px-6 py-4 text-sm text-gray-500 items-start text-start capitalize min-w-[250px]">
                                        <div className="flex flex-col gap-1 items-start">
                                            <span className="text-gray-900 capitalize items-start text-left">
                                                Course: {application.qualification}
                                            </span>
                                            <span className="text-gray-900 capitalize">
                                                TTC: {application.training_hours}
                                            </span>
                                            <span className="text-gray-900 capitalize">
                                                Exp: {application.teaching_experience}
                                            </span>
                                        </div>
                                    </td> */}
                  <td className="px-6 py-4 text-sm text-gray-500 text-left min-w-[250px]">
                    {application.ai_validation && application.ai_validation.ttc_hours ? (
                      <div className="flex flex-col gap-1 items-start text-left">
                        <span className="text-gray-900 capitalize text-left">
                          Course: {application.ai_validation.highest_degree}
                        </span>
                        <span className="text-gray-900 capitalize">
                          TTC: {application.ai_validation.ttc_hours}
                        </span>
                        <span className="text-gray-900 capitalize">
                          Exp: {application.ai_validation.total_teacher_experience_years} yrs
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-900 capitalize text-left">Pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap items-start text-sm">
                    <div className="flex flex-col gap-2 text-start">
                      <div className="flex flex-row gap-2 items-center">
                        {application.nextStatusAvailable &&
                          editIcon({
                            className:
                              'w-5 h-5 text-blue-500 hover:text-blue-800 ml-2 cursor-pointer',
                            onClick: () => {
                              setNewStatus('');
                              setShowEditStatus(application);
                            }
                          })}
                        <span className="capitalize">{application.status}</span>
                      </div>
                      <span className="capitalize">
                        Application Count: {application.application_count}{' '}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col text-start gap-2">
                      <div
                        className="flex flex-row text-blue-600 gap-1 cursor-pointer items-start text-left"
                        onClick={() => {
                          handleViewMore(application);
                        }}
                      >
                        {historyIcon({
                          className: 'w-5 h-5 '
                        })}
                        <span>View History</span>
                      </div>
                      {/* custom mail */}
                      <div
                        className="flex flex-row justify-start items-center gap-1 cursor-pointer text-blue-600"
                        onClick={() => {
                          setMailInfo({
                            show: true,
                            subject: `Regarding your application`,
                            body: `Hello ${application.full_name},\n\n`,
                            to: application
                          });
                          handleCommentClick(application);
                        }}
                      >
                        <span>
                          {sendMailIcon({
                            className: 'w-6 h-6 p-0'
                          })}
                        </span>
                        <span>Compose Mail</span>
                      </div>
                      {/* base pay */}
                      <div className="flex flex-row gap-2 items-center font-semibold">
                        {showEditPayIcon !== application.uuid && (
                          <div className="flex flex-row gap-1 items-start cursor-pointer">
                            {editIcon({
                              className: 'w-5 h-5 text-blue-600',
                              onClick: () => {
                                setShowEditPayIcon(application.uuid);
                              }
                            })}
                            {application.base_pay === '' ? (
                              <label
                                onClick={() => {
                                  setShowEditPayIcon(application.uuid);
                                }}
                                className="capitalize text-blue-600 cursor-pointer"
                              >
                                Add Base Pay
                              </label>
                            ) : (
                              `${application.base_pay} INR`
                            )}
                          </div>
                        )}

                        {showEditPayIcon === application.uuid && (
                          <>
                            <input
                              type="text"
                              value={editPay}
                              className="border border-gray-300 rounded-md px-2 py-1 w-24"
                              onChange={(e) => handleEditPayChange(e.target.value)}
                            />
                            {undoIcon({
                              className:
                                'w-5 h-5 text-red-400 border-0 bg-white hover:text-red-800 cursor-pointer',
                              onClick: () => {
                                setShowEditPayIcon('');
                              }
                            })}
                            {saveIcon({
                              className:
                                'w-5 h-5 text-green-400 border-0 bg-white hover:text-green-800 cursor-pointer',
                              onClick: handleEditPay
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {applications.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No applications found</p>
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistoryModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 capitalize">
                Application History - {selectedApplication.full_name}
              </h2>
              <button
                onClick={closeHistoryModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold cursor-pointer"
              >
                x
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changed By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applicationHistory.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-start">
                          {moment(record.created_date).format('lll')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-start">
                          {record.changed_by_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-start">
                          {record.reason}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-start capitalize">
                          {record.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Empty State for History */}
              {(!applicationHistory || applicationHistory.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No history available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 flex justify-end">
              <button
                onClick={closeHistoryModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {mailInfo.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[100vh] overflow-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Send Mail to {mailInfo.to.full_name}
              </h2>
              <button
                onClick={() => setMailInfo({ show: false, subject: '', body: '' })}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold cursor-pointer"
              >
                x
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="flex flex-col gap-5">
                <div className="flex flex-row justify-start items-center gap-4">
                  <span className="w-48">Templated Email</span>
                  <select
                    id="new-status"
                    value={mailTemplate.name}
                    onChange={(e) => {
                      const template = mailTemplates.find((t) => t.name === e.target.value) || {
                        name: 'Custom',
                        subject: '',
                        body: ''
                      };

                      setMailTemplate(template);
                      if (template.name !== 'Custom') {
                        setMailInfo({
                          ...mailInfo,
                          subject: template.subject,
                          body: template.body.replace('[Applicant Name]', mailInfo.to.full_name)
                        });
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option value="" disabled hidden>
                      Select mail template
                    </option>
                    {mailTemplates.map((option) => (
                      <option
                        key={`edit-status-${option.name}`}
                        value={option.name}
                        className="capitalize"
                      >
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-row justify-between items-center gap-4">
                  <label className="block text-sm font-semibold text-gray-700 w-48">Subject</label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded-md focus:outline-none px-2 py-1"
                    value={mailInfo.subject}
                    onChange={(e) => {
                      setMailInfo({ ...mailInfo, subject: e.target.value });
                    }}
                    placeholder="Mail Subject"
                    maxLength={100}
                  />
                </div>

                <label className="block text-sm font-semibold text-gray-700">Content</label>
                <textarea
                  id="body-input"
                  className="border border-gray-300 rounded-md focus:outline-none px-2 py-1 h-48"
                  type="text"
                  onChange={(e) => {
                    setMailInfo({ ...mailInfo, body: e.target.value });
                  }}
                  value={mailInfo.body}
                  placeholder="Mail Body"
                  maxLength={500}
                />

                <button onClick={handleSendMail}>Send</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* status modal */}
      {showEditStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Application History - {showEditStatus.full_name}
              </h2>
              <button
                onClick={() => setShowEditStatus(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold cursor-pointer"
              >
                x
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="flex flex-col gap-5">
                <span className="capitalize text-sm text-gray-500">
                  Current Status: <span className="font-semibold">{showEditStatus.status}</span>
                </span>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 capitalize">
                    New Status
                  </label>

                  <select
                    id="new-status"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option value="" disabled hidden>
                      Select status
                    </option>
                    {editStatusOptions.map((option) => (
                      <option
                        key={`edit-status-${option.value}`}
                        value={option.value}
                        className="capitalize"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <textarea
                  id="reason-input"
                  className="border border-gray-300 rounded-md focus:outline-none px-2 py-1 h-24"
                  type="text"
                  placeholder="Reason for status change"
                  maxLength={100}
                />

                <button onClick={handleStatusChange}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherApplication;
